import { FC } from "react";
import {
	FormControl,
	InputLabel,
	MenuItem,
	Select,
	Stack,
} from "@mui/material";
import { Dayjs } from "dayjs";
import BasicDatepicker from "../../../../components/BasicDatepicker/BasicDatepicker";

export interface ReportFilterOption {
	id: string;
	name: string;
}

export interface ReportFilters {
	orgUnitId: string;
	researcherId: string;
	startDate: Dayjs | null;
	endDate: Dayjs | null;
}

export type ReportFilterKey =
	| "orgUnit"
	| "researcher"
	| "startDate"
	| "endDate";

interface ReportFiltersBarProps {
	idPrefix: string;
	filters: ReportFilters;
	onChange: (next: ReportFilters) => void;
	availableOrgUnits?: ReportFilterOption[];
	availableResearchers?: ReportFilterOption[];
	// Hide specific filters for tabs that don't apply them (e.g. SIMS-5801
	// Educational engagement only takes date filters per AC).
	hiddenFilters?: ReportFilterKey[];
}

// Filter row shared by every sub-report tab inside the consolidated report
// page. The four filters (Org unit + Researcher + Start date + End date)
// are identical across reports per the SIMS-5777 spec, so this component
// owns the layout and label conventions in one place.
const ReportFiltersBar: FC<ReportFiltersBarProps> = ({
	idPrefix,
	filters,
	onChange,
	availableOrgUnits = [],
	availableResearchers = [],
	hiddenFilters = [],
}) => {
	const update = (patch: Partial<ReportFilters>): void =>
		onChange({ ...filters, ...patch });
	const isHidden = (key: ReportFilterKey): boolean =>
		hiddenFilters.includes(key);

	return (
		<Stack
			direction={{ xs: "column", md: "row" }}
			spacing={2}
			alignItems={{ xs: "stretch", md: "center" }}
		>
			{!isHidden("orgUnit") && (
				<FormControl size="small" sx={{ minWidth: 220 }}>
					<InputLabel id={`${idPrefix}-orgunit-label`}>Org unit</InputLabel>
					<Select
						labelId={`${idPrefix}-orgunit-label`}
						label="Org unit"
						value={filters.orgUnitId}
						onChange={(e) => update({ orgUnitId: e.target.value as string })}
					>
						<MenuItem value="">All org units</MenuItem>
						{availableOrgUnits.map((o) => (
							<MenuItem key={o.id} value={o.id}>
								{o.name}
							</MenuItem>
						))}
					</Select>
				</FormControl>
			)}

			{!isHidden("researcher") && (
				<FormControl size="small" sx={{ minWidth: 220 }}>
					<InputLabel id={`${idPrefix}-researcher-label`}>
						Researcher
					</InputLabel>
					<Select
						labelId={`${idPrefix}-researcher-label`}
						label="Researcher"
						value={filters.researcherId}
						onChange={(e) => update({ researcherId: e.target.value as string })}
					>
						<MenuItem value="">All researchers</MenuItem>
						{availableResearchers.map((r) => (
							<MenuItem key={r.id} value={r.id}>
								{r.name}
							</MenuItem>
						))}
					</Select>
				</FormControl>
			)}

			{!isHidden("startDate") && (
				<BasicDatepicker
					label="Start date"
					value={filters.startDate}
					onChange={(v) => update({ startDate: v })}
					onClear={() => update({ startDate: null })}
					sx={{ minWidth: 200, maxWidth: 220 }}
				/>
			)}

			{!isHidden("endDate") && (
				<BasicDatepicker
					label="End date"
					value={filters.endDate}
					onChange={(v) => update({ endDate: v })}
					onClear={() => update({ endDate: null })}
					sx={{ minWidth: 200, maxWidth: 220 }}
				/>
			)}
		</Stack>
	);
};

export default ReportFiltersBar;
